import { Component, OnInit, Input, Inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import { Store } from '@ngrx/store';
import * as fromStore from '@usimweb/state/store';
import { Projects, Env } from '@usimweb/core/models';

@Component({
    selector: 'app-request-support-dialog',
    templateUrl: './request-support-dialog.component.html',
    styleUrls: ['./request-support-dialog.component.scss'],
    standalone: false
})
export class RequestSupportDialogComponent implements OnInit {
  @Input() data = { uuid: '', projects: null };

  public projects!: Projects;

  public technicalSupportMessage: string = '';
  public supportRequests = {
    technicalSupport: {} as any,
    simulationSupport: {} as any,
    materialSupport: {} as any,
  };

  constructor(
    @Inject('environment') private environment: Env,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<RequestSupportDialogComponent>,
    private store: Store<fromStore.DashboardState>
  ) {}

  public simulationRequestForm = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    email: new UntypedFormControl(''),
    message: new UntypedFormControl(''),
  });

  public materialRequestForm = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    email: new UntypedFormControl(''),
    contactSustainableTeam: new UntypedFormControl(false),
    message: new UntypedFormControl(''),
  });

  ngOnInit(): void {
    // FIX ME molded viewer has no guard
    // load data from store if viewer guard exists
    if (this.data.projects) {
      this.projects = this.data.projects;
    }

    this.store.select(fromStore.getProjectById(this.data.uuid)).subscribe((project) => {
      // FIX ME molded viewer has no guard
      // load data from store if viewer guard exists
      if (project) {
        this.projects = project;
      }
      this.renderSupportRequestInfo();
    });
  }

  getUpdatedProjectData() {
    const projectId = this.projects.uuid;

    this.store.select(fromStore.getProjectById(projectId)).subscribe((project) => {
      // FIX ME molded viewer has no guard
      // load data from store if viewer guard exists
      if (project) {
        this.projects = project;
      }
      this.renderSupportRequestInfo();
    });
  }

  renderSupportRequestInfo() {
    let supportRequestInfo = this.projects.support_requests_info;
    let allowStatuses = ['new_request', 'in_progress'];

    // loop through support request info and set the available support requests
    for (const supportRequest of supportRequestInfo) {
      if (supportRequest.request_type === 'technical' && allowStatuses.includes(supportRequest.status)) {
        this.supportRequests.technicalSupport = supportRequest;
      } else if (supportRequest.request_type === 'simulation' && allowStatuses.includes(supportRequest.status)) {
        this.supportRequests.simulationSupport = supportRequest;
      } else if (supportRequest.request_type === 'material' && allowStatuses.includes(supportRequest.status)) {
        this.supportRequests.materialSupport = supportRequest;
      }
    }
  }

  // close the dialog and return updated project data
  closeDialog(): void {
    this.dialogRef.close({ 'projects': this.projects });
  }

  // request technical support
  technicalSupport() {
    const requestData = {
      customer_mail: '',
      customer_name: '',
      message: this.technicalSupportMessage,
      request_type: 'technical',
      uuid: this.data.uuid,
    };

    // check response for request api
    // check for support_requests_info obj in reducer
    // obj check is like an project specific updated
    this.store.dispatch(new fromStore.RequestSupport({ uuid: this.data.uuid, reqSupport: requestData }));
    this.getUpdatedProjectData();
  }

  // request simulation support
  simulationSupport() {
    let requestData = {
      uuid: this.projects.uuid,
      request_type: 'simulation',
      message: this.simulationRequestForm.get('message')?.value,
      customer_name: this.simulationRequestForm.get('name')?.value,
      customer_email: this.simulationRequestForm.get('email')?.value,
    };

    this.store.dispatch(new fromStore.RequestSupport({ uuid: this.data.uuid, reqSupport: requestData }));
    this.getUpdatedProjectData();
  }

  // request material support
  materialSupport() {
    let requestData = {
      uuid: this.projects.uuid,
      request_type: 'material',
      message: this.materialRequestForm.get('message')?.value,
      customer_name: this.materialRequestForm.get('name')?.value,
      customer_email: this.materialRequestForm.get('email')?.value,
      contact_sustainable_team: this.materialRequestForm.get('contactSustainableTeam')?.value,
    };

    this.store.dispatch(new fromStore.RequestSupport({ uuid: this.data.uuid, reqSupport: requestData }));
    this.getUpdatedProjectData();
  }

  // unrequest technical support
  unrequestTechnicalSupport() {
    let requestData = {
      uuid: this.projects.uuid,
      request_id: this.supportRequests.technicalSupport['request_id'],
    };

    this.store.dispatch(new fromStore.UnrequestSupport(requestData));
    this.getUpdatedProjectData();
  }

  // unrequest any support type
  public unrequestSupport(supportType: any) {
    let requestData = { uuid: this.projects.uuid, request_id: null as unknown as string };

    if (supportType === 'technical') {
      requestData.request_id = this.supportRequests.technicalSupport['request_id'];
    } else if (supportType === 'simulation') {
      requestData.request_id = this.supportRequests.simulationSupport['request_id'];
    } else if (supportType === 'material') {
      requestData.request_id = this.supportRequests.materialSupport['request_id'];
    }

    this.store.dispatch(new fromStore.UnrequestSupport(requestData));
    this.getUpdatedProjectData();

    if (supportType === 'technical') {
      this.supportRequests.technicalSupport = {};
    } else if (supportType === 'simulation') {
      this.supportRequests.simulationSupport = {};
    } else if (supportType === 'material') {
      this.supportRequests.materialSupport = {};
    }
  }

  openTermsofUse() {
    const url = 'https://ultrasimweb.basf.com/' + this.environment.APP + '/terms_of_use/?lang=en';
    window.open(url, '_blank');
  }

  techSupport() {
    return Object.entries(this.supportRequests.technicalSupport).length > 0;
  }

  simSupport() {
    return Object.entries(this.supportRequests.simulationSupport).length > 0;
  }

  matSupport() {
    return Object.entries(this.supportRequests.materialSupport).length > 0;
  }

  public save() {
    this.simulationSupport();
  }

  public close() {
    this.dialogRef.close(true);
  }
}
